import React, { useState } from 'react';

const AddExpense = ({ onAddExpense, categories = [], user = {} }) => {
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    billFile: null,
    billPreview: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [showTable, setShowTable] = useState(true);

  // Default categories if none provided
  const defaultCategories = [
    { id: 'food', name: 'Food & Dining', icon: '🍔', color: 'bg-blue-50 text-blue-700' },
    { id: 'transport', name: 'Transportation', icon: '🚗', color: 'bg-blue-50 text-blue-700' },
    { id: 'shopping', name: 'Shopping', icon: '🛍️', color: 'bg-blue-50 text-blue-700' },
    { id: 'bills', name: 'Bills & Utilities', icon: '📄', color: 'bg-blue-50 text-blue-700' },
    { id: 'entertainment', name: 'Entertainment', icon: '🎬', color: 'bg-blue-50 text-blue-700' },
    { id: 'health', name: 'Healthcare', icon: '🏥', color: 'bg-blue-50 text-blue-700' },
    { id: 'education', name: 'Education', icon: '📚', color: 'bg-blue-50 text-blue-700' },
    { id: 'other', name: 'Other', icon: '📦', color: 'bg-blue-50 text-blue-700' },
  ];

  const activeCategories = categories.length > 0 ? categories : defaultCategories;

  // Mock user data (replace with actual user data from props)
  const currentUser = {
    name: user.name || 'John Doe',
    designation: user.designation || 'Software Engineer',
    department: user.department || 'IT',
    employeeId: user.employeeId || 'EMP001',
    ...user
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, billFile: 'File size should be less than 5MB' }));
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, billFile: 'Only JPG, PNG, PDF files are allowed' }));
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({ 
        ...prev, 
        billFile: file,
        billPreview: previewUrl
      }));
      setErrors(prev => ({ ...prev, billFile: '' }));
    }
  };

  const removeBill = () => {
    if (formData.billPreview) {
      URL.revokeObjectURL(formData.billPreview);
    }
    setFormData(prev => ({ 
      ...prev, 
      billFile: null,
      billPreview: null
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 2) {
      newErrors.title = 'Title must be at least 2 characters';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(formData.amount) || formData.amount <= 0) {
      newErrors.amount = 'Please enter a valid positive amount';
    } else if (parseFloat(formData.amount) > 9999999) {
      newErrors.amount = 'Amount is too large';
    }

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      if (selectedDate > today) {
        newErrors.date = 'Date cannot be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const expenseData = {
        id: Date.now(),
        title: formData.title.trim(),
        amount: parseFloat(formData.amount),
        category: formData.category,
        date: formData.date,
        notes: formData.notes.trim(),
        createdAt: new Date().toISOString(),
        time: new Date().toLocaleTimeString(),
        employeeName: currentUser.name,
        designation: currentUser.designation,
        department: currentUser.department,
        employeeId: currentUser.employeeId,
        billFile: formData.billFile ? {
          name: formData.billFile.name,
          type: formData.billFile.type,
          size: formData.billFile.size,
          preview: formData.billPreview
        } : null,
      };

      // Add to local expenses list
      setExpenses(prev => [expenseData, ...prev]);

      // Call parent component's handler
      if (onAddExpense) {
        await onAddExpense(expenseData);
      }

      // Reset form after successful submission
      setFormData({
        title: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        billFile: null,
        billPreview: null,
      });
      
      console.log('Expense added:', expenseData);
    } catch (error) {
      console.error('Error adding expense:', error);
      setErrors({ submit: 'Failed to add expense. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (formData.billPreview) {
      URL.revokeObjectURL(formData.billPreview);
    }
    setFormData({
      title: '',
      amount: '',
      category: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      billFile: null,
      billPreview: null,
    });
    setErrors({});
  };

  const handleDeleteExpense = (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      setExpenses(prev => prev.filter(expense => expense.id !== id));
    }
  };

  // Get category name from id
  const getCategoryName = (categoryId) => {
    const category = activeCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Get category icon
  const getCategoryIcon = (categoryId) => {
    const category = activeCategories.find(cat => cat.id === categoryId);
    return category ? category.icon : '💰';
  };

  // Get selected category info for display
  const selectedCategory = activeCategories.find(cat => cat.id === formData.category);

  // Calculate total expenses
  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);

  // Format currency in Rupees
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
      {/* Add Expense Form */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        {/* Header - Blue Color */}
        <div className="bg-blue-600 px-6 py-6 sm:px-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Add New Expense</h1>
          <p className="text-blue-100 mt-2 text-sm sm:text-base">
            Track your spending and manage your finances
          </p>
        </div>

        {/* User Info Bar */}
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">👤 {currentUser.name}</span>
            <span className="text-gray-600">💼 {currentUser.designation}</span>
            <span className="text-gray-600">🏢 {currentUser.department}</span>
          </div>
          <div className="text-sm text-gray-500">
            ID: {currentUser.employeeId}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-red-700 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Title Field */}
<div>
  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
    Expense Title <span className="text-red-500">*</span>
  </label>
  <input
    type="text"
    id="title"
    name="title"
    value={formData.title}
    onChange={handleChange}
    maxLength="100"
    placeholder="e.g., Grocery shopping, Movie tickets, Gas station (Max 100 characters)"
    className={`w-full px-4 py-2 border ${errors.title ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} focus:outline-none focus:ring-1 focus:border-blue-500 transition-all duration-200 rounded`}
  />
  {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
  {/* Optional: Character counter */}
  <div className="text-right text-xs text-gray-500 mt-1">
    {formData.title?.length || 0}/100 characters
  </div>
</div>

          {/* Amount and Date Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                  ₹
                </span>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  className={`w-full pl-8 pr-4 py-2 border ${errors.amount ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} focus:outline-none focus:ring-1 focus:border-blue-500 transition-all duration-200 rounded`}
                />
              </div>
              {errors.amount && <p className="mt-1 text-sm text-red-500">{errors.amount}</p>}
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={`w-full px-4 py-2 border ${errors.date ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} focus:outline-none focus:ring-1 focus:border-blue-500 transition-all duration-200 rounded`}
              />
              {errors.date && <p className="mt-1 text-sm text-red-500">{errors.date}</p>}
            </div>
          </div>

          {/* Category Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {activeCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: category.id }))}
                  className={`flex flex-col items-center gap-2 p-3 transition-all duration-200 rounded ${
                    formData.category === category.id
                      ? 'bg-blue-600 text-white ring-2 ring-offset-2 ring-blue-600'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <span className="text-2xl">{category.icon}</span>
                  <span className="text-xs font-medium text-center">{category.name}</span>
                </button>
              ))}
            </div>
            {errors.category && <p className="mt-2 text-sm text-red-500">{errors.category}</p>}
          </div>

          {/* Selected Category Display */}
          {selectedCategory && formData.category && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded border border-blue-200">
              <span className="text-2xl">{selectedCategory.icon}</span>
              <div>
                <p className="text-sm text-gray-600">Selected Category</p>
                <p className="font-medium text-gray-800">{selectedCategory.name}</p>
              </div>
            </div>
          )}

          {/* Bill Upload Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Bill/Receipt
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors duration-200">
              <div className="space-y-1 text-center">
                {formData.billPreview ? (
                  <div className="space-y-3">
                    <div className="relative inline-block">
                      {formData.billFile?.type?.startsWith('image/') ? (
                        <img 
                          src={formData.billPreview} 
                          alt="Bill preview" 
                          className="max-h-48 rounded-lg shadow-md object-contain"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-lg">
                          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm text-gray-600">{formData.billFile?.name}</span>
                          <span className="text-xs text-gray-500">
                            {(formData.billFile?.size / 1024).toFixed(2)} KB
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={removeBill}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m-4-4l4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="bill-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        <span>Upload a file</span>
                        <input
                          id="bill-upload"
                          name="bill-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, PDF up to 5MB
                    </p>
                  </>
                )}
              </div>
            </div>
            {errors.billFile && <p className="mt-1 text-sm text-red-500">{errors.billFile}</p>}
          </div>

          {/* Notes Field */}
<div>
  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
    Notes (Optional)
  </label>
  <textarea
    id="notes"
    name="notes"
    value={formData.notes}
    onChange={handleChange}
    rows="3"
    maxLength="200"
    placeholder="Add any additional details about this expense... (Max 200 characters)"
    className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 rounded resize-none"
  />
  {/* Optional: Character counter */}
  <div className="text-right text-xs text-gray-500 mt-1">
    {formData.notes?.length || 0}/200 characters
  </div>
</div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 bg-blue-600 text-white font-medium py-2 px-6 rounded transition-all duration-200 ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding Expense...
                </span>
              ) : (
                'Add Expense'
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 bg-gray-100 text-gray-700 font-medium py-2 px-6 rounded hover:bg-gray-200 transition-all duration-200 border border-gray-300"
            >
              Clear Form
            </button>
          </div>
        </form>
      </div>

      {/* Expenses Table */}
      {showTable && expenses.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Expense History</h2>
              <p className="text-sm text-gray-500 mt-1">Track all your submitted expenses</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalExpenses)}</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense, index) => (
                  <tr key={expense.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{expense.employeeName}</div>
                      <div className="text-xs text-gray-500">ID: {expense.employeeId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{expense.designation}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {expense.department}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                      <div className="truncate">{expense.title}</div>
                      {expense.notes && (
                        <div className="text-xs text-gray-500 truncate">{expense.notes}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{getCategoryIcon(expense.category)}</span>
                        <span className="text-sm text-gray-600">{getCategoryName(expense.category)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(expense.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {expense.billFile ? (
                        <button
                          onClick={() => window.open(expense.billFile.preview, '_blank')}
                          className="text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center gap-1"
                        >
                          📎 View Bill
                        </button>
                      ) : (
                        <span className="text-gray-400">No bill</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(expense.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {expense.time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteExpense(expense.id)}
                        className="text-red-600 hover:text-red-800 transition-colors duration-200"
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-right text-sm font-medium text-gray-700">
                    Total:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                    {formatCurrency(totalExpenses)}
                  </td>
                  <td colSpan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* No Expenses Message */}
      {showTable && expenses.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">💰</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Expenses Yet</h3>
          <p className="text-gray-500">Start by adding your first expense using the form above.</p>
        </div>
      )}
    </div>
  );
};

export default AddExpense;